/**
 * Button component that opens the attachment compliance modal
 * Displays summary and opens nested modal on click
 */

import React, { useState } from 'react';
import { Paperclip, ChevronRight } from 'lucide-react';
import { AttachmentComplianceModal } from './AttachmentComplianceModal';

interface FilterResult {
  action: 'ALLOW' | 'WARN' | 'AMEND' | 'BLOCK';
  amendment?: string | null;
  internal_monologue?: string;
  reason?: string;
  resource_violations?: Record<string, unknown>;
}

interface AttachmentResult {
  file_id: number | string;
  name?: string;
  filename?: string;
  content_type?: string;
  action: 'ALLOW' | 'WARN' | 'AMEND' | 'BLOCK';
  filter_result?: FilterResult;
  reason?: string;
}

interface AttachmentComplianceButtonProps {
  attachmentResults: AttachmentResult[];
  overallAction?: string;
  amendedFileIds?: (string | number)[];
}

export const AttachmentComplianceButton: React.FC<AttachmentComplianceButtonProps> = ({
  attachmentResults,
  overallAction,
  amendedFileIds = [],
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Count actions for summary
  const actionCounts = attachmentResults.reduce(
    (acc, attachment) => {
      const action = attachment.filter_result?.action || attachment.action;
      acc[action] = (acc[action] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const getSummaryText = () => {
    const parts: string[] = [];
    if (actionCounts.BLOCK) parts.push(`${actionCounts.BLOCK} blocked`);
    if (actionCounts.AMEND) parts.push(`${actionCounts.AMEND} amended`);
    if (actionCounts.WARN) parts.push(`${actionCounts.WARN} warned`);
    if (actionCounts.ALLOW) parts.push(`${actionCounts.ALLOW} allowed`);
    return parts.join(', ');
  };

  const getOverallBadgeColor = () => {
    if (overallAction === 'BLOCK')
      return 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700';
    if (overallAction === 'AMEND')
      return 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700';
    if (overallAction === 'WARN')
      return 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700';
    return 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700';
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-all hover:shadow-md bg-gray-50 dark:bg-gray-800/50 group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Paperclip size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                Attachment Compliance Details
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({attachmentResults.length} file{attachmentResults.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
                {overallAction && (
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getOverallBadgeColor()}`}
                  >
                    {overallAction}
                  </span>
                )}
                <span>{getSummaryText()}</span>
              </div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className="text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
          />
        </div>
      </button>

      <AttachmentComplianceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        attachmentResults={attachmentResults}
        overallAction={overallAction}
        amendedFileIds={amendedFileIds}
      />
    </>
  );
};
